import { ENDPOINTS } from '../constants/endpoints'
import type { AxiosResponse } from 'axios'
import type { User } from '@/features/users/types/user'
import type { LoginData } from '../schema/loginSchema'
import type {
  LoginResponse,
  PasswordUpdateData,
  ResetPasswordData,
} from '../types/auth'
import axiosInstance from '@/integrations/axios/axios-instance'

interface RefreshTokenResponse {
  user: User
  accessToken: string
  refreshToken: string
  expiresAt: string
}

interface AuthApi {
  login: (loginData: LoginData) => Promise<AxiosResponse<LoginResponse>>
  forgotPassword: (email: string) => Promise<AxiosResponse<{ message: string }>>
  resetPassword: (
    data: ResetPasswordData,
  ) => Promise<AxiosResponse<{ message: string }>>
  updatePassword: (
    data: PasswordUpdateData,
  ) => Promise<AxiosResponse<{ message: string }>>
  refreshToken: (token: string) => Promise<AxiosResponse<RefreshTokenResponse>>
  resendToken: (email: string) => Promise<AxiosResponse<{ message: string }>>
  resendVerification: (
    email: string,
  ) => Promise<AxiosResponse<{ message: string }>>
  me: () => Promise<AxiosResponse<{ user: User }>>
  logout: () => Promise<AxiosResponse<{ message: string }>>
  checkSetupToken: (
    token: string,
  ) => Promise<AxiosResponse<{ isVerified: boolean; message?: string }>>
}

export const authApi: AuthApi = {
  login: (loginData: LoginData): Promise<AxiosResponse<LoginResponse>> => {
    const endpoint = ENDPOINTS.LOGIN
    return axiosInstance.post(endpoint, loginData)
  },

  forgotPassword: (
    email: string,
  ): Promise<AxiosResponse<{ message: string }>> => {
    const endpoint = ENDPOINTS.FORGOT_PASSWORD
    return axiosInstance.post(endpoint, { email })
  },

  resetPassword: (
    data: ResetPasswordData,
  ): Promise<AxiosResponse<{ message: string }>> => {
    const endpoint = ENDPOINTS.RESET_PASSWORD
    return axiosInstance.post(endpoint, data)
  },

  updatePassword: (
    data: PasswordUpdateData,
  ): Promise<AxiosResponse<{ message: string }>> => {
    const endpoint = ENDPOINTS.UPDATE_PASSWORD
    return axiosInstance.put(endpoint, data)
  },

  refreshToken: (
    token: string,
  ): Promise<AxiosResponse<RefreshTokenResponse>> => {
    const endpoint = ENDPOINTS.REFRESH_TOKEN
    return axiosInstance.post(endpoint, { refreshToken: token })
  },

  resendToken: (email: string): Promise<AxiosResponse<{ message: string }>> => {
    const endpoint = ENDPOINTS.RESEND_TOKEN
    return axiosInstance.post(endpoint, { email })
  },

  resendVerification: (
    email: string,
  ): Promise<AxiosResponse<{ message: string }>> => {
    const endpoint = ENDPOINTS.RESEND_VERIFICATION
    return axiosInstance.post(endpoint, { email })
  },

  me: (): Promise<AxiosResponse<{ user: User }>> => {
    const endpoint = ENDPOINTS.ME
    return axiosInstance.get(endpoint)
  },

  logout: (): Promise<AxiosResponse<{ message: string }>> => {
    const endpoint = ENDPOINTS.LOGOUT
    return axiosInstance.post(endpoint)
  },

  checkSetupToken: (
    token: string,
  ): Promise<AxiosResponse<{ isVerified: boolean; message?: string }>> => {
    const endpoint = `${ENDPOINTS.CHECK_SETUP_TOKEN}?token=${token}`
    return axiosInstance.get(endpoint)
  },
}
